import {
  forwardRef,
  type ReactNode,
  useCallback,
  useEffect,
  useImperativeHandle,
  useReducer,
  useRef,
  useState,
} from "react";

import classNames from "classnames";
import { createPortal } from "react-dom";

import { ReactComponent as CloseIcon24 } from "../../assets/icons/CloseIcon24.svg";
import { useBottomSheetDrag } from "../../hooks/useBottomSheetDrag";
import { Button } from "../Button/Button";
import { Spinner } from "../Spinner/Spinner";

// eslint-disable-next-line postcss-modules/no-unused-class
import style from "./Modal.module.css";

const ScaleAnimation = {
  keyframes: [
    {
      transform: "scale(1)",
    },
    {
      transform: "scale(1.03)",
    },
  ] satisfies Keyframe[] | PropertyIndexedKeyframes,
  options: {
    duration: 75,
    iterations: 1,
  } satisfies number | KeyframeAnimationOptions,
};

type ModalState = "opening" | "visible" | "closing" | "hidden";

type ModalStatus = { status: ModalState };

type Action =
  | { type: "OPEN" }
  | { type: "CLOSE" }
  | { type: "FINISH_OPENING" }
  | { type: "FINISH_CLOSING" };

const modalReducer = (state: ModalStatus, action: Action): ModalStatus => {
  switch (action.type) {
    case "OPEN":
      if (state.status === "hidden") {
        return { status: "opening" };
      }
      return state;
    case "CLOSE":
      if (state.status === "visible") {
        return { status: "closing" };
      }
      return state;
    case "FINISH_OPENING":
      if (state.status === "opening") {
        return { status: "visible" };
      }
      return state;
    case "FINISH_CLOSING":
      if (state.status === "closing") {
        return { status: "hidden" };
      }
      return state;
    default:
      return state;
  }
};

export type ModalProps = {
  children: ReactNode;
  footer?: ReactNode;
  className?: string;
  size?: "small" | "medium" | "large";
  title?: string;
  persistent?: boolean;
  preventCloseOnEscape?: boolean;
  isLoading?: boolean;
  open: boolean;
  onClose?: () => void;
  onCloseStart?: () => void;
  onOpen?: () => void;
  background?: ReactNode;
  draggable?: boolean;
};

export const Modal = forwardRef<HTMLElement, ModalProps>(
  (
    {
      children,
      footer,
      className,
      size = "medium",
      title,
      persistent = false,
      preventCloseOnEscape = false,
      isLoading = false,
      open,
      onClose,
      onCloseStart,
      onOpen,
      background,
      draggable = false,
    },
    ref
  ) => {
    const modalRef = useRef<HTMLDivElement>(null);
    useImperativeHandle(ref, () => modalRef.current as HTMLDivElement);
    const contentRef = useRef<HTMLDivElement>(null);
    const isDragClosingRef = useRef(false);

    const [modalState, dispatch] = useReducer(modalReducer, {
      status: open ? "visible" : "hidden",
    });
    const [isContentOverflowing, setIsContentOverflowing] = useState(false);

    const handleDragClose = useCallback(() => {
      isDragClosingRef.current = true;
      onCloseStart?.();
      dispatch({ type: "CLOSE" });
      dispatch({ type: "FINISH_CLOSING" });
      onClose?.();
    }, [onCloseStart, onClose]);

    const { sheetRef, handleProps } = useBottomSheetDrag({
      onClose: handleDragClose,
      enabled: draggable,
      resetStylesOnClose: false,
    });

    const disableScroll = () => {
      const scrollbarWidth = window.innerWidth - document.body.clientWidth;
      document.body.style.overflow = "hidden";
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    };

    const enableScroll = () => {
      document.body.style.overflow = "auto";
      document.body.style.paddingRight = "0";
    };
    const handleClose = useCallback(() => {
      if (modalState.status === "visible" || modalState.status === "opening") {
        onCloseStart?.();
        dispatch({ type: "CLOSE" });
      }
    }, [modalState.status, onCloseStart]);

    const handleAnimationEnd = useCallback(() => {
      switch (modalState.status) {
        case "opening":
          dispatch({ type: "FINISH_OPENING" });
          onOpen?.();
          break;
        case "closing":
          dispatch({ type: "FINISH_CLOSING" });
          onClose?.();
          break;
      }
    }, [modalState.status, onOpen, onClose]);

    useEffect(() => {
      if (modalState.status === "hidden") {
        enableScroll();
      } else {
        disableScroll();
      }
    }, [modalState.status]);

    useEffect(() => {
      if (open) {
        isDragClosingRef.current = false;
        dispatch({ type: "OPEN" });
      } else {
        handleClose();
      }
    }, [handleClose, open]);

    useEffect(() => {
      const checkOverflow = () => {
        if (contentRef.current) {
          const hasOverflow = contentRef.current.scrollHeight > contentRef.current.clientHeight;
          setIsContentOverflowing(hasOverflow);
        }
      };

      if (contentRef.current) {
        const resizeObserver = new ResizeObserver(checkOverflow);
        resizeObserver.observe(contentRef.current);

        return () => {
          resizeObserver.disconnect();
        };
      }
    }, [children]);

    useEffect(() => {
      if (
        modalState.status === "visible" &&
        modalRef.current &&
        !modalRef.current.contains(document.activeElement)
      ) {
        modalRef.current.focus();
      }
    }, [modalState.status]);

    useEffect(() => {
      const handleKeyDown = (event: KeyboardEvent) => {
        if ((event.key !== "Escape" && event.key !== "Esc") || !open) {
          return;
        }

        if (modalRef.current && modalRef.current.contains(document.activeElement)) {
          event.stopPropagation();
          if (!preventCloseOnEscape) {
            handleClose();
          } else {
            modalRef.current?.animate(ScaleAnimation.keyframes, ScaleAnimation.options);
          }
        }
      };

      document.addEventListener("keydown", handleKeyDown);
      return () => document.removeEventListener("keydown", handleKeyDown);
    }, [handleClose, open, preventCloseOnEscape]);

    return (
      <>
        {modalState.status !== "hidden" &&
          createPortal(
            <div className={style.wrapper} data-state={modalState.status}>
              <div
                className={style.overlay}
                data-state={modalState.status}
                onClick={() => {
                  if (!persistent) handleClose();
                }}
              ></div>
              <div
                className={classNames(className, style.modal, style[size])}
                data-state={modalState.status}
                onAnimationEnd={handleAnimationEnd}
                role="dialog"
                aria-modal="true"
                ref={(node) => {
                  (modalRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
                  (sheetRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
                }}
                tabIndex={-1}
              >
                {background && <div className={style.background}>{background}</div>}
                {draggable && (
                  <div className={style.dragHandle} {...handleProps}>
                    <div className={style.dragHandleBar} />
                  </div>
                )}
                <div className={style.header}>
                  {title && <div className={style.title}>{title}</div>}
                  {!persistent && (
                    <Button
                      className={style.close}
                      onClick={() => {
                        handleClose();
                      }}
                      variant="borderless"
                      aria-label="Close"
                    >
                      <CloseIcon24 />
                    </Button>
                  )}
                </div>
                {isLoading && (
                  <div className={style.loadingContent}>
                    <Spinner size="sm" />
                  </div>
                )}
                {!isLoading && (
                  <div className={style.content}>
                    <div className={classNames(style.contentInner, !title && style.fullHeightContent)}>
                      {children}
                    </div>
                  </div>
                )}
                {footer && (
                  <div
                    className={classNames(
                      style.footer,
                      isContentOverflowing && style.footerWithBorder
                    )}
                  >
                    {footer}
                  </div>
                )}
              </div>
            </div>,
            document.getElementById("modal-root") ?? document.body
          )}
      </>
    );
  }
);

Modal.displayName = "Modal";
